import { Box, Button, Checkbox, Flex, Icon, Input, Popover, PopoverBody, PopoverContent, PopoverTrigger, Portal, Text, useColorModeValue } from "@chakra-ui/react"
import { useMemo, useRef, useState } from "react"
import { MdArrowDropDown } from "react-icons/md"
import { tokenFilter } from "lib/utils"
import { pinChosen, sortByLabel } from "lib/sort"

const MaybePortal = ({ enabled, children }) => enabled ? <Portal>{children}</Portal> : children

// Drop-down filter with a search field and multiple choice.
// - options:  [{ value, label }]
// - value:    array of the chosen values (empty = no filter, "any")
// - onChange: called with the new array
// - label:    text of the "nothing chosen" state, e.g. "Any group"
// - inDrawer: set when the filter is used inside a drawer (a form). A Chakra drawer keeps focus
//   and scrolling inside itself, so the list must not be drawn outside of it (no Portal); it
//   then opens in place, like components/select/SearchSelect.
export default function MultiSelectFilter({ label, options, value, onChange, width = "200px", inDrawer = false }) {
  const [term, setTerm] = useState("")
  const searchRef = useRef(null)
  const bg = useColorModeValue("secondaryGray.300", "navy.900")
  const textColor = useColorModeValue("gray.700", "gray.100")
  const popoverBg = useColorModeValue("white", "navy.800")

  const chosen = value || []
  // The options are listed by name (Serbian alphabet, "Group 9" before "Group 10")
  const sorted = useMemo(() => sortByLabel(options), [options])
  // With an empty search the options that are chosen when the list is opened come first. That is
  // decided when the list opens (`openedWith`), not on every tick, so a line does not jump away
  // from the mouse while it is being ticked.
  const [openedWith, setOpenedWith] = useState([])
  const shown = useMemo(() => {
    const matching = sorted.filter(option => tokenFilter(option.label, term))
    return term.trim() ? matching : pinChosen(matching, option => openedWith.includes(option.value))
  }, [sorted, term, openedWith])

  const toggle = (optionValue) => {
    onChange(chosen.includes(optionValue) ? chosen.filter(v => v !== optionValue) : [...chosen, optionValue])
  }

  // "Select shown" adds everything the search currently shows, "Clear" removes only that.
  const selectShown = () => onChange([...new Set([...chosen, ...shown.map(o => o.value)])])
  const clearShown = () => onChange(chosen.filter(v => !shown.some(o => o.value === v)))

  const summary = chosen.length === 0
    ? label
    : chosen.length === 1
      ? (options.find(o => o.value === chosen[0])?.label || label)
      : `${chosen.length} selected`

  return (
    <Popover
      placement="bottom-start"
      isLazy
      onOpen={() => setOpenedWith(chosen)}
      onClose={() => setTerm("")}
      initialFocusRef={inDrawer ? searchRef : undefined}
      strategy={inDrawer ? "fixed" : undefined}
    >
      <PopoverTrigger>
        <Button
          size="sm"
          variant="outline"
          bg={bg}
          color={chosen.length === 0 ? "gray.400" : textColor}
          fontWeight="500"
          borderRadius="30px"
          w={width}
          maxW="100%"
          justifyContent="space-between"
          rightIcon={<Icon as={MdArrowDropDown} w={5} h={5} />}
        >
          <Text as="span" isTruncated>{summary}</Text>
        </Button>
      </PopoverTrigger>
      <MaybePortal enabled={!inDrawer}>
        <PopoverContent
          w="260px"
          maxW="90vw"
          bg={popoverBg}
          _focus={{ boxShadow: "none" }}
          rootProps={inDrawer ? { zIndex: "popover" } : undefined}
          // Escape closes only this list; it must not reach the drawer and close the whole form
          onKeyDown={inDrawer ? (e => { if (e.key === "Escape") e.stopPropagation() }) : undefined}
        >
          <PopoverBody p={3}>
            <Input ref={searchRef} size="sm" borderRadius="30px" placeholder="Search..." value={term} onChange={e => setTerm(e.target.value)} />
            <Flex justify="space-between" mt={2} mb={1}>
              <Button size="xs" variant="ghost" colorScheme="brand" onClick={selectShown} isDisabled={shown.length === 0}>Select shown</Button>
              <Button size="xs" variant="ghost" colorScheme="brand" onClick={clearShown} isDisabled={!shown.some(o => chosen.includes(o.value))}>Clear</Button>
            </Flex>
            <Box maxH="260px" overflowY="auto">
              {shown.map(option => (
                <Box key={option.value} py={1}>
                  <Checkbox colorScheme="brand" size="sm" isChecked={chosen.includes(option.value)} onChange={() => toggle(option.value)}>
                    <Text as="span" fontSize="sm">{option.label}</Text>
                  </Checkbox>
                </Box>
              ))}
              {shown.length === 0 && <Text fontSize="sm" color="gray.400" py={2}>Nothing found</Text>}
            </Box>
          </PopoverBody>
        </PopoverContent>
      </MaybePortal>
    </Popover>
  )
}
